/**
 * Front desk check-ins ("reception visits").
 *
 * One doc per visitor per day in `receptionVisits/{id}`; the lead carries a
 * copy of the current one in `lead.receptionVisit` so the lead drawer and
 * My Today can show "At reception" / "With Vishal" without another query.
 *
 * Status: waiting (checked in) → with (a sales manager took it) → done
 * (presenter saved the Visit log) | left (went away before anyone met them).
 */
import { useEffect, useState } from 'react';
import { collection, doc, increment, onSnapshot, query, runTransaction, serverTimestamp, where } from 'firebase/firestore';
import type { Firestore, Timestamp } from 'firebase/firestore';

export type ReceptionStatus = 'waiting' | 'with' | 'done' | 'left';
export type VisitWith = 'alone' | 'family' | 'friend' | 'other';
export type CameVia = 'walk-in' | 'broker' | 'c2c';
export type VisitSourceType = 'direct' | 'broker' | 'c2c' | 'other' | 'unknown';

export interface VisitSourceCorrection {
  from: Record<string, unknown>;
  to: Record<string, unknown>;
  correctedAt?: Timestamp | null;
  correctedBy?: string | null;
}

export interface ReceptionVisit {
  id: string;
  dayKey: string;
  status: ReceptionStatus;
  leadId: string;
  name: string;
  phone10: string;
  isNewLead: boolean;
  projectId: string | null;
  projectName: string | null;
  visitWith: VisitWith | null;
  visitWithOther: string | null;
  peopleCount: number;
  cameVia: CameVia | null;
  brokerId: string | null;
  brokerPresent?: boolean;
  brokerName: string | null;
  brokerPhone10?: string | null;
  agencyName: string | null;
  c2cName: string | null;
  /** Latest correction to front-desk attribution; the original fields remain above. */
  sourceCorrection?: VisitSourceCorrection | null;
  /** Scheduled site visit time when the visitor had one today. */
  bookedAt: Timestamp | null;
  ownerEmail: string | null;
  ownerName: string | null;
  checkedInAt: Timestamp | null;
  checkedInBy: string | null;
  takenAt: Timestamp | null;
  presenterEmail: string | null;
  presenterName: string | null;
  finishedAt: Timestamp | null;
  visitActivityId: string | null;
  finalDiscussionNotes: string[] | null;
  leftAt: Timestamp | null;
  leftReason: ReceptionDepartureReason | null;
  leftNote: string | null;
}

export type ReceptionDepartureReason = 'no-time' | 'sample-flat-only' | 'left-before-meeting' | 'other';

export const RECEPTION_DEPARTURE_REASONS: Array<{ key: ReceptionDepartureReason; label: string }> = [
  { key: 'no-time', label: 'No time for a meeting' },
  { key: 'sample-flat-only', label: 'Viewed the sample flat only' },
  { key: 'left-before-meeting', label: 'Left before being attended' },
  { key: 'other', label: 'Other' },
];

export const receptionDepartureLabel = (reason: ReceptionDepartureReason | null) =>
  RECEPTION_DEPARTURE_REASONS.find((item) => item.key === reason)?.label || 'Left without meeting';

export const VISIT_WITH_OPTIONS: Array<{ key: VisitWith; label: string }> = [
  { key: 'alone', label: 'Alone' },
  { key: 'family', label: 'Family' },
  { key: 'friend', label: 'Relative' },
  { key: 'other', label: 'Other' },
];

export const CAME_VIA_OPTIONS: Array<{ key: CameVia; label: string }> = [
  { key: 'walk-in', label: 'Walk-in' },
  { key: 'broker', label: 'Broker' },
  { key: 'c2c', label: 'C2C' },
];

/** One-tap points for a site visit note (Finish visit + Log activity → Visit). */
export const VISIT_QUICK_POINTS = [
  'Liked location',
  'Price is high',
  'Wants payment plan',
  'Will bring family',
  'Comparing other projects',
  'Wants bigger unit',
];

/** "2026-10-22" in India time. */
export const dayKeyIST = (d = new Date()) =>
  new Date(d.getTime() + 330 * 60000).toISOString().slice(0, 10);

export const toDate = (v: any): Date | null => (v?.toDate ? v.toDate() : null);

export const minutesSince = (v: any, now = new Date()) => {
  const d = toDate(v);
  return d ? Math.max(0, Math.round((now.getTime() - d.getTime()) / 60000)) : 0;
};

export const timeLabel = (v: any) => {
  const d = v instanceof Date ? v : toDate(v);
  return d ? d.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' }) : '';
};

const visitWithLabel = (r: Pick<ReceptionVisit, 'visitWith' | 'peopleCount'>) => {
  if (!r.visitWith) return '';
  if (r.visitWith === 'alone') return 'alone';
  const base = r.visitWith === 'family' ? 'family' : r.visitWith === 'friend' ? 'with relative' : 'with others';
  return r.peopleCount > 1 ? `${base} of ${r.peopleCount}` : base;
};

/** "Walk-in · Swarna Lagom · alone" style one-liner. */
export const receptionSummary = (r: ReceptionVisit) => {
  const corrected = r.sourceCorrection?.to || {};
  const sourceType = String(corrected.sourceType || (r.cameVia === 'broker' ? 'broker' : r.cameVia === 'c2c' ? 'c2c' : r.cameVia === 'walk-in' ? 'direct' : 'unknown'));
  const brokerName = String(corrected.brokerName || (r.cameVia === 'broker' ? r.brokerName || '' : ''));
  const brokerAgency = String(corrected.brokerAgencyName || (r.cameVia === 'broker' ? r.agencyName || '' : ''));
  const referrerName = String(corrected.customerReferrerName || (r.cameVia === 'c2c' ? r.c2cName || '' : ''));
  const sourceNote = String(corrected.note || '');
  return [
    r.bookedAt ? `Scheduled ${timeLabel(r.bookedAt)}` : null,
    sourceType === 'direct' ? (r.isNewLead ? 'Walk-in · new lead' : 'Walk-in') : null,
    r.projectName,
    visitWithLabel(r),
    sourceType === 'broker' ? `Broker ${[brokerAgency, brokerName].filter(Boolean).join(' · ') || '—'}` : null,
    sourceType === 'c2c' ? `C2C ${referrerName}` : null,
    sourceType === 'other' ? `Other · ${sourceNote}` : null,
    sourceType === 'unknown' ? 'Source unknown' : null,
  ]
    .filter(Boolean)
    .join(' · ');
};

/** Admin correction while a visitor is waiting or in a meeting. */
export const correctActiveVisitSource = async (
  db: Firestore,
  visit: ReceptionVisit,
  to: { sourceType: 'direct' | 'broker' | 'c2c'; brokerId?: string | null; brokerName?: string | null; brokerAgencyName?: string | null; customerReferrerName?: string | null },
  byEmail: string
) => {
  if (!byEmail || !['direct', 'broker', 'c2c'].includes(to.sourceType)) throw new Error('Choose a visit source.');
  if (to.sourceType === 'broker' && (!to.brokerId || (!to.brokerName && !to.brokerAgencyName))) throw new Error('Choose the broker.');
  if (to.sourceType === 'c2c' && !to.customerReferrerName?.trim()) throw new Error('Enter the C2C reference name.');
  const visitRef = doc(db, 'receptionVisits', visit.id);
  const leadRef = doc(db, 'leads', visit.leadId);
  const activityRef = doc(collection(leadRef, 'activities'));
  await runTransaction(db, async (tx) => {
    const [visitSnap, leadSnap] = await Promise.all([tx.get(visitRef), tx.get(leadRef)]);
    const current = visitSnap.data();
    const leadVisit = leadSnap.data()?.receptionVisit;
    if (!current || !['waiting', 'with'].includes(String(current.status)) ||
        !leadSnap.exists() || leadVisit?.id !== visit.id || leadVisit.status !== current.status) {
      throw new Error('This visit is no longer active. Refresh Today and try again.');
    }
    const original = {
      sourceType: current.cameVia === 'walk-in' ? 'direct' : current.cameVia || 'unknown',
      brokerId: current.cameVia === 'broker' ? current.brokerId || null : null,
      brokerName: current.cameVia === 'broker' ? current.brokerName || null : null,
      brokerAgencyName: current.cameVia === 'broker' ? current.agencyName || null : null,
      customerReferrerName: current.cameVia === 'c2c' ? current.c2cName || null : null,
    };
    const from = current.sourceCorrection?.to || original;
    const next = {
      sourceType: to.sourceType,
      brokerId: to.sourceType === 'broker' ? to.brokerId || null : null,
      brokerName: to.sourceType === 'broker' ? to.brokerName || null : null,
      brokerAgencyName: to.sourceType === 'broker' ? to.brokerAgencyName || null : null,
      customerReferrerName: to.sourceType === 'c2c' ? to.customerReferrerName?.trim() || null : null,
    };
    const same = (value: Record<string, any>) =>
      Object.keys(next).every((key) => String(value[key] || '') === String((next as Record<string, any>)[key] || ''));
    if (same(from)) return;
    const correction = { from, to: next, correctedAt: serverTimestamp(), correctedBy: byEmail };
    const label = (value: Record<string, any>) => value.sourceType === 'broker'
      ? `Broker${value.brokerName ? ` ${value.brokerName}` : ''}`
      : value.sourceType === 'c2c' ? `C2C${value.customerReferrerName ? ` ${value.customerReferrerName}` : ''}`
        : value.sourceType === 'direct' ? 'Walk-in' : String(value.sourceType || 'Unknown');
    tx.update(visitRef, { sourceCorrection: correction });
    tx.update(leadRef, {
      'receptionVisit.sourceCorrection': correction,
      activityCount: increment(1), updatedAt: serverTimestamp(), updatedBy: byEmail, lastActivityAt: serverTimestamp(),
    });
    tx.set(activityRef, {
      type: 'internal', event: 'reception-source-corrected', receptionVisitId: visit.id,
      visitSourceCorrection: { from, to: next },
      notes: [`Visit source corrected while ${current.status === 'waiting' ? 'waiting at reception' : 'in a meeting'}: ${label(from)} → ${label(next)}.`],
      byEmail, at: serverTimestamp(),
    });
  });
};

const fromDoc = (id: string, d: Record<string, any>): ReceptionVisit => ({
  id,
  dayKey: String(d.dayKey || ''),
  status: (d.status || 'waiting') as ReceptionStatus,
  leadId: String(d.leadId || ''),
  name: String(d.name || 'Visitor'),
  phone10: String(d.phone10 || ''),
  isNewLead: d.isNewLead === true,
  projectId: d.projectId || null,
  projectName: d.projectName || null,
  visitWith: d.visitWith || null,
  visitWithOther: typeof d.visitWithOther === 'string' ? d.visitWithOther : null,
  peopleCount: Number(d.peopleCount || 1),
  cameVia: d.cameVia || null,
  brokerId: d.brokerId || null,
  brokerPresent: d.brokerPresent === true,
  brokerName: d.brokerName || null,
  brokerPhone10: d.brokerPhone10 || null,
  agencyName: d.agencyName || null,
  c2cName: d.c2cName || null,
  sourceCorrection: d.sourceCorrection || null,
  bookedAt: d.bookedAt || null,
  ownerEmail: d.ownerEmail || null,
  ownerName: d.ownerName || null,
  checkedInAt: d.checkedInAt || null,
  checkedInBy: d.checkedInBy || null,
  takenAt: d.takenAt || null,
  presenterEmail: d.presenterEmail || null,
  presenterName: d.presenterName || null,
  finishedAt: d.finishedAt || null,
  visitActivityId: d.visitActivityId || null,
  finalDiscussionNotes: Array.isArray(d.finalDiscussionNotes) ? d.finalDiscussionNotes.filter((note: unknown): note is string => typeof note === 'string' && !!note.trim()) : null,
  leftAt: d.leftAt || null,
  leftReason: RECEPTION_DEPARTURE_REASONS.some((item) => item.key === d.leftReason) ? d.leftReason : null,
  leftNote: typeof d.leftNote === 'string' ? d.leftNote : null,
});

/** Today's check-ins, oldest first (live). */
export const useReceptionToday = (db: Firestore, enabled = true) => {
  const [rows, setRows] = useState<ReceptionVisit[]>([]);
  const [dayKey, setDayKey] = useState(dayKeyIST());

  // Roll over at midnight without a reload.
  useEffect(() => {
    const timer = window.setInterval(() => setDayKey(dayKeyIST()), 60000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!enabled) {
      setRows([]);
      return undefined;
    }
    return onSnapshot(
      query(collection(db, 'receptionVisits'), where('dayKey', '==', dayKey)),
      (snap) =>
        setRows(
          snap.docs
            .map((d) => fromDoc(d.id, d.data()))
            .sort((a, b) => (toDate(a.checkedInAt)?.getTime() ?? 0) - (toDate(b.checkedInAt)?.getTime() ?? 0))
        ),
      () => setRows([])
    );
  }, [db, dayKey, enabled]);

  return rows;
};

/**
 * "I'll take it": the first sales manager to tap becomes the presenter.
 * A transaction stops two people taking the same visitor.
 */
export const takeReceptionVisit = async (
  db: Firestore,
  visit: { id: string; leadId: string; name: string },
  presenter: { email: string; name: string }
) =>
  runTransaction(db, async (tx) => {
    const ref = doc(db, 'receptionVisits', visit.id);
    const snap = await tx.get(ref);
    const data = snap.data() || {};
    if (data.status !== 'waiting') {
      throw new Error(data.presenterName ? `${data.presenterName} already took ${visit.name}.` : `${visit.name} is no longer waiting.`);
    }
    tx.update(ref, { status: 'with', takenAt: serverTimestamp(), presenterEmail: presenter.email, presenterName: presenter.name });
    tx.update(doc(db, 'leads', visit.leadId), {
      'receptionVisit.status': 'with',
      'receptionVisit.takenAt': serverTimestamp(),
      'receptionVisit.presenterEmail': presenter.email,
      'receptionVisit.presenterName': presenter.name,
    });
  });

/** Undo an accidental take without losing the original reception check-in. */
export const returnReceptionVisit = async (
  db: Firestore,
  visit: { id: string; leadId: string; name: string },
  actor: { email: string; isAdmin: boolean }
) =>
  runTransaction(db, async (tx) => {
    const visitRef = doc(db, 'receptionVisits', visit.id);
    const leadRef = doc(db, 'leads', visit.leadId);
    const [visitSnap, leadSnap] = await Promise.all([tx.get(visitRef), tx.get(leadRef)]);
    const current = visitSnap.data();
    if (!current || current.status !== 'with') {
      throw new Error(`${visit.name} is no longer in a meeting.`);
    }
    if (!actor.isAdmin && String(current.presenterEmail || '').toLowerCase() !== actor.email.toLowerCase()) {
      throw new Error('Only the presenter or an admin can return this visitor to reception.');
    }
    const leadVisit = leadSnap.data()?.receptionVisit;
    if (!leadSnap.exists() || leadVisit?.id !== visit.id || leadVisit?.status !== 'with') {
      throw new Error('The lead has a newer reception state. Refresh Today before trying again.');
    }
    const reset = { status: 'waiting', takenAt: null, presenterEmail: null, presenterName: null };
    tx.update(visitRef, reset);
    tx.update(leadRef, {
      'receptionVisit.status': reset.status,
      'receptionVisit.takenAt': reset.takenAt,
      'receptionVisit.presenterEmail': reset.presenterEmail,
      'receptionVisit.presenterName': reset.presenterName,
      lastActivityAt: serverTimestamp(),
    });
    tx.set(doc(collection(leadRef, 'activities')), {
      type: 'internal',
      event: 'reception-returned',
      notes: ['Returned to reception after a meeting was accepted by mistake.'],
      byEmail: actor.email,
      at: serverTimestamp(),
    });
  });

/** Record a visitor who left reception without a presenter meeting. */
export const leaveReceptionVisit = async (
  db: Firestore,
  visit: { id: string; leadId: string; name: string },
  departure: { reason: ReceptionDepartureReason; note: string; byEmail: string }
) => {
  const reason = RECEPTION_DEPARTURE_REASONS.find((item) => item.key === departure.reason);
  const note = departure.note.trim();
  if (!reason || (reason.key === 'other' && !note)) throw new Error('Choose a reason and describe other reasons.');
  if (note.length > 500) throw new Error('Keep the note under 500 characters.');
  const visitRef = doc(db, 'receptionVisits', visit.id);
  const leadRef = doc(db, 'leads', visit.leadId);
  const activityRef = doc(collection(leadRef, 'activities'));
  await runTransaction(db, async (tx) => {
    const [visitSnap, leadSnap] = await Promise.all([tx.get(visitRef), tx.get(leadRef)]);
    const currentVisit = visitSnap.data();
    if (!currentVisit || currentVisit.status !== 'waiting') {
      throw new Error(`${visit.name} is no longer waiting at reception.`);
    }
    const leadVisit = leadSnap.data()?.receptionVisit;
    if (!leadSnap.exists() || leadVisit?.id !== visit.id || leadVisit?.status !== 'waiting') {
      throw new Error('The lead has a newer reception state. Refresh before trying again.');
    }
    const update = {
      status: 'left',
      leftAt: serverTimestamp(),
      leftReason: reason.key,
      leftNote: note || null,
      leftBy: departure.byEmail,
    };
    tx.update(visitRef, update);
    tx.update(leadRef, {
      'receptionVisit.status': update.status,
      'receptionVisit.leftAt': update.leftAt,
      'receptionVisit.leftReason': update.leftReason,
      'receptionVisit.leftNote': update.leftNote,
      'receptionVisit.leftBy': update.leftBy,
      lastActivityAt: serverTimestamp(),
    });
    tx.set(activityRef, {
      type: 'internal',
      event: 'reception-left',
      notes: [`Left reception without a presenter meeting: ${reason.label}.${note ? ` ${note}` : ''}`],
      byEmail: departure.byEmail,
      at: serverTimestamp(),
    });
  });
};

// Same search helpers the Add Lead form writes, so walk-ins are searchable.
export const buildPhoneSearchParts = (phone10: string): string[] => {
  const digits = String(phone10 || '').replace(/\D/g, '');
  if (digits.length !== 10) return [];
  const out = new Set<string>();
  for (let len = 3; len <= 10; len += 1) {
    for (let i = 0; i + len <= 10; i += 1) out.add(digits.slice(i, i + len));
  }
  return Array.from(out);
};

export const normalizeNameLower = (v: string) => v.trim().replace(/\s+/g, ' ').toLowerCase();

export const buildTrigrams = (value: string): string[] => {
  const s = normalizeNameLower(value);
  if (!s) return [];
  const out = new Set<string>();
  const padded = ` ${s} `;
  for (let i = 0; i + 3 <= padded.length; i += 1) {
    const gram = padded.slice(i, i + 3);
    if (gram.trim()) out.add(gram);
  }
  return Array.from(out);
};
